'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useAppDispatch } from '@akinon/next/redux/hooks';
import { resetHeaderState } from '@akinon/next/redux/reducers/header';
import { closeMiniBasket } from '@akinon/next/redux/reducers/root';
import { ROUTES } from '@theme/routes';
import { pushPageView } from '@theme/utils/gtm';
import Script from 'next/script';
import { RootState } from '@theme/redux/store';
import { useAppSelector } from '@akinon/next/redux/hooks';
import { WalletPrefetch } from '@theme/views/wallet-prefetch';
import { ProductRatingsProvider } from '@theme/hooks/use-product-ratings';

export default function RootTemplate({
  children
}: {
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const URL = process.env.NEXT_PUBLIC_URL || 'https://www.fashfed.com';
  const { isMobileApp } = useAppSelector((state: RootState) => state.root);

  useEffect(() => {
    dispatch(closeMiniBasket());
    dispatch(resetHeaderState());
  }, [dispatch, pathname, searchParams]);

  useEffect(() => {
    if (pathname === ROUTES.HOME) {
      window.scrollTo(0, 0);
    }
    if (isMobileApp) {
      return;
    }
    pushPageView(pathname);
  }, [pathname, isMobileApp]);

  const jsonLdWebSite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: URL,
    name: 'FashFed',
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: `${URL}/list/?search_text={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    ]
  };

  const jsonLdOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: URL,
    name: 'FashFed',
    logo: `${URL}/images/logo.svg`
  };

  return (
    <>
      {/* Google Tag Manager */}
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];
              w[l].push({'event' : window?.localStorage?.getItem('isMobileApp') ? 'app_view_event' : 'web_view_event' });
              w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
              w[l].push({'event': 'pageView','page': window?.location.pathname});
              var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;
              j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;
              f?.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-K92G9G8Z');
          `
        }}
      />
      {/* End Google Tag Manager */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebSite) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdOrganization) }}
      />
      <script src="/barcode/a.out.js" async />
      <WalletPrefetch />
      <ProductRatingsProvider>{children}</ProductRatingsProvider>
    </>
  );
}
